import { useState, useEffect } from 'react';
import { Play, FileText, Sparkles } from 'lucide-react';
import { ProtectedAudio, ProtectedImage, ProtectedVideo, InlinePdf } from './ProtectedMedia';

export const LessonContent = ({ lesson }) => {
  const [activeTab, setActiveTab] = useState('points'); // 'points' | 'pdf'

  useEffect(() => {
    setActiveTab('points');
  }, [lesson.id]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Imagen de apoyo: lo primero que ve el usuario */}
      {(() => {
        const images = (Array.isArray(lesson.images) ? lesson.images : []).filter((im) => im && im.url);
        if (images.length === 0) return null;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {images.map((im, i) => (
              <ProtectedImage key={im.id || i} src={im.url} alt={`Imagen de apoyo ${i + 1}`} caption={im.caption} />
            ))}
          </div>
        );
      })()}

      {/* Video */}
      <div style={{
        background: '#0a1a2a',
        border: '1px solid rgba(197, 160, 89, 0.3)',
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
        position: 'relative',
        aspectRatio: '16/9'
      }}>
        {lesson.video_url ? (
          <ProtectedVideo
            src={lesson.video_url}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: '#0f2035', gap: '12px'
          }}>
            <Play className="w-12 h-12" style={{ color: 'var(--text-muted)' }} />
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Video en procesamiento para esta lección.</p>
          </div>
        )}
      </div>

      {/* Material de apoyo: audio (solo lectura) */}
      {(() => {
        const audioUrl = lesson.audio_url || '';
        if (!audioUrl) return null;
        return (
          <div style={{ background: '#0f2035', border: '1px solid rgba(197, 160, 89, 0.2)', borderRadius: '14px', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--gold-matte)', fontFamily: 'monospace', textTransform: 'uppercase', fontWeight: 'bold' }}>
              📎 Material de apoyo
            </span>
            {lesson.audio_caption && (
              <p style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', margin: '0 0 6px' }}>
                {lesson.audio_caption}
              </p>
            )}
            <ProtectedAudio src={audioUrl} style={{ width: '100%' }} />
          </div>
        );
      })()}

      {/* Infografía (solo lectura) */}
      {(() => {
        const infographicUrl = lesson.infographic_url || '';
        if (!infographicUrl) return null;
        return (
          <div style={{ background: '#0f2035', border: '1px solid rgba(197, 160, 89, 0.2)', borderRadius: '14px', padding: '1.25rem' }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--gold-matte)', fontFamily: 'monospace', textTransform: 'uppercase', fontWeight: 'bold' }}>
              🖼️ Infografía (solo lectura)
            </span>
            <ProtectedImage src={infographicUrl} alt="Infografía de la lección" caption="Infografía · solo lectura" />
          </div>
        );
      })()}

      {/* Presentación de diapositivas (solo lectura) */}
      {(() => {
        const slidesUrl = lesson.slides_url || '';
        if (!slidesUrl) return null;
        return (
          <div style={{ background: '#0f2035', border: '1px solid rgba(197, 160, 89, 0.2)', borderRadius: '14px', padding: '1.25rem' }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--gold-matte)', fontFamily: 'monospace', textTransform: 'uppercase', fontWeight: 'bold' }}>
              📑 Presentación de diapositivas (solo lectura)
            </span>
            <InlinePdf url={slidesUrl} name="Presentación de diapositivas (solo lectura)" />
          </div>
        );
      })()}

      {/* Título */}
      <div>
        <span style={{ fontSize: '0.72rem', color: 'var(--gold-matte)', fontFamily: 'monospace', textTransform: 'uppercase', fontWeight: 'bold' }}>
          {lesson.module}
        </span>
        <h1 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1.4rem', color: '#fff', margin: '4px 0 0' }}>
          {lesson.title}
        </h1>
      </div>

      {/* Pestañas de lectura */}
      <div style={{ background: '#0f2035', border: '1px solid rgba(197, 160, 89, 0.2)', borderRadius: '14px', overflow: 'hidden' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(197, 160, 89, 0.2)', background: 'rgba(10, 26, 42, 0.6)', overflowX: 'auto' }}>
          {[
            { id: 'points', label: 'Puntos Clave', icon: Sparkles },
            { id: 'pdf', label: `Material PDF (${lesson.pdf_resources?.length || 0})`, icon: FileText },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`btn-vx-cat${activeTab === tab.id ? ' active' : ''}`}
                style={{ borderRadius: 0, border: 'none', borderBottom: activeTab === tab.id ? '2px solid var(--gold-matte)' : 'none', padding: '12px 20px', whiteSpace: 'nowrap' }}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div style={{ padding: '1.25rem' }}>
          {/* PUNTOS CLAVE */}
          {activeTab === 'points' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <h3 className="vx-modal-sec-title">📌 Resumen Doctrinal y Puntos de Evaluación</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {lesson.key_points && lesson.key_points.length > 0 ? (
                  lesson.key_points.map((point, index) => (
                    <div key={index} style={{
                      padding: '10px 14px', borderRadius: '8px', background: 'rgba(10, 26, 42, 0.6)',
                      border: '1px solid rgba(197, 160, 89, 0.15)', fontSize: '0.8rem', color: 'var(--text-secondary)',
                      display: 'flex', alignItems: 'flex-start', gap: '10px', lineHeight: 1.6, textAlign: 'justify'
                    }}>
                      <span style={{
                        width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(197, 160, 89, 0.2)',
                        color: 'var(--gold-matte)', fontSize: '0.7rem', fontWeight: 'bold', fontFamily: 'monospace',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px'
                      }}>
                        {index + 1}
                      </span>
                      <span>{point}</span>
                    </div>
                  ))
                ) : (
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No hay puntos clave destacados.</p>
                )}
              </div>
            </div>
          )}

          {/* PDF */}
          {activeTab === 'pdf' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <h3 className="vx-modal-sec-title">📄 Documentos de Lectura en PDF (solo lectura)</h3>
              {lesson.pdf_resources && lesson.pdf_resources.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {lesson.pdf_resources.map((pdf, pIdx) => (
                    <InlinePdf key={pdf.id || pIdx} url={pdf.url} name={pdf.name || `Documento ${pIdx + 1}`} />
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No hay archivos PDF adjuntos en esta lección.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
